import {noop} from '@benchling/js-utils';
import {Alert, Button, Group, Stack, Text} from '@mantine/core';
import React, {useEffect, useReducer, useRef, useState} from 'react';

import {
  connectionRequiresUserLogin,
  ConnectionStatus,
  InstrumentServiceDetails,
} from 'instruments/connections/ConnectionConstants';
import importInstrumentDataModalReducer from 'instruments/instrument-data/importInstrumentDataModalReducer';
import {INITIAL_STATE, InstrumentQueryStatus} from 'instruments/instrument-data/InstrumentDataConstants';
import {
  createCopyObjectInstrumentQueryFromRun,
  pollInstrumentQueryStatus,
} from 'instruments/instrument-data/InstrumentDataDAO';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {FileListingGraphQL} from '../../../graphql-fragments/files/__generated/FileListingGraphQL';
import OnDemandLoginSection from './OnDemandLoginSection';
import {
  CopyObjectResponseParams,
  getLoginParams,
  getRetrievalErrorMessage,
  PendingRetrieval,
  searchForRetrievedFile,
} from './onDemandRetrieval';

enum RetrievalStatus {
  IDLE = 'IDLE',
  RETRIEVING = 'RETRIEVING',
  SUCCEEDED = 'SUCCEEDED',
  FAILED = 'FAILED',
}

type RetrievalState =
  | {status: RetrievalStatus.IDLE}
  | {status: RetrievalStatus.RETRIEVING}
  | {status: RetrievalStatus.SUCCEEDED; fileName: string; isSelected: boolean}
  | {status: RetrievalStatus.FAILED; message: string};

interface DirectRequestRetrievalPanelProps {
  connectionId: string;
  instrumentServiceDetails: InstrumentServiceDetails;
  runApiId: string;
  // Why the user can't retrieve more data right now, e.g. because they've selected the maximum number of
  // files.
  disabledReason: string | null;
  // Adds the retrieved file to the selection. Returns false if it couldn't be added because the maximum number
  // of files is already selected.
  onRetrieveFile: (fileId: string) => boolean;
  onPendingRetrievalChange: (pendingRetrieval: PendingRetrieval | null) => void;
}

/**
 * Retrieves data for the run's fields from a direct-request connection (e.g. Empower or AVEVA PI), which
 * returns one file per request rather than listing files to choose from.
 */
export default function DirectRequestRetrievalPanel({
  connectionId,
  instrumentServiceDetails,
  runApiId,
  disabledReason,
  onRetrieveFile,
  onPendingRetrievalChange,
}: DirectRequestRetrievalPanelProps): JSX.Element {
  const [loginState, loginDispatch] = useReducer(importInstrumentDataModalReducer, INITIAL_STATE);
  const [retrievalState, setRetrievalState] = useState<RetrievalState>({status: RetrievalStatus.IDLE});
  // A request can outlive the panel, e.g. when the user switches connections mid-request. Once the panel is
  // gone, its result must not change the selection or clear another panel's pending retrieval.
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      onPendingRetrievalChange(null);
    };
  }, [onPendingRetrievalChange]);

  const isLoggedIn = loginState.loginQueryStatus === InstrumentQueryStatus.DONE;
  if (connectionRequiresUserLogin(instrumentServiceDetails) && !isLoggedIn) {
    return <OnDemandLoginSection connectionId={connectionId} state={loginState} dispatch={loginDispatch} />;
  }

  const isConnectionAvailable = instrumentServiceDetails.status.value === ConnectionStatus.AVAILABLE;
  const isRetrieving = retrievalState.status === RetrievalStatus.RETRIEVING;

  const retrieveFile = async (): Promise<FileListingGraphQL> => {
    const {instrumentQueryId} = await createCopyObjectInstrumentQueryFromRun(
      connectionId,
      {runApiId},
      getLoginParams(loginState.loginUsername)
    );
    const query = await pollInstrumentQueryStatus(instrumentQueryId);
    if (query.status !== InstrumentQueryStatus.DONE) {
      throw new Error(query.info || 'The connection reported an error without details.');
    }
    const path = (query.params as CopyObjectResponseParams | undefined)?.path;
    if (!path) {
      throw new Error('The connection did not return a file. Please contact Benchling support.');
    }
    return await searchForRetrievedFile(path, connectionId);
  };

  const handleRetrieve = (): void => {
    setRetrievalState({status: RetrievalStatus.RETRIEVING});
    const retrieval = retrieveFile().then(
      (file) => {
        if (isMountedRef.current) {
          setRetrievalState({
            status: RetrievalStatus.SUCCEEDED,
            fileName: file.name || file.scratchFile.filename,
            isSelected: onRetrieveFile(file.id),
          });
        }
        return file.id;
      },
      (error: unknown) => {
        const message = getRetrievalErrorMessage(error);
        if (isMountedRef.current) {
          setRetrievalState({status: RetrievalStatus.FAILED, message});
        }
        throw new Error(`Couldn't retrieve data from the connection: ${message}`);
      }
    );
    // If the user confirms their selection mid-request, wait for the file before selecting it.
    onPendingRetrievalChange({retrieve: async () => [await retrieval]});
    retrieval.catch(noop).finally(() => {
      if (isMountedRef.current) {
        onPendingRetrievalChange(null);
      }
    });
  };

  return (
    <Stack spacing="sm">
      {!isConnectionAvailable && (
        <Alert variant="warning">Data cannot be retrieved while the connection is unavailable.</Alert>
      )}
      <Group spacing="sm">
        <Button
          variant="secondary"
          leftIcon={<BenchlingIcon icon="download" />}
          loading={isRetrieving}
          disabled={!isConnectionAvailable || disabledReason != null}
          onClick={handleRetrieve}
        >
          {isRetrieving ? 'Retrieving data...' : 'Retrieve data'}
        </Button>
        <Text size="sm" color="dimmed">
          {isRetrieving
            ? 'Requesting data for this run from the connection. This can take a couple of minutes.'
            : (disabledReason ?? "Requests the data for this run's fields and selects the file it returns.")}
        </Text>
      </Group>
      {retrievalState.status === RetrievalStatus.SUCCEEDED &&
        (retrievalState.isSelected ? (
          <Alert variant="success">{`Retrieved ${retrievalState.fileName} and selected it.`}</Alert>
        ) : (
          <Alert variant="warning">
            {`Retrieved ${retrievalState.fileName}. Remove a selected file to select it.`}
          </Alert>
        ))}
      {retrievalState.status === RetrievalStatus.FAILED && (
        <Alert variant="error" title="Couldn't retrieve data from the connection">
          {retrievalState.message}
        </Alert>
      )}
    </Stack>
  );
}
